
import { Form, InputGroup, Button, Card } from 'react-bootstrap';
import AppLoader from 'components/general/AppLoader';
import { toast } from 'react-toastify';
import { useSelector } from "react-redux";
import { useEffect, useState } from 'react';
import Select from 'components/general/ViewportSelect';

export default function AgendaItemEditModal({ item, getMeeting, show, handleClose }) {
    // Track theme for live Select style updates
    const [theme, setTheme] = useState(document.body.getAttribute('data-theme') || 'light');
    useEffect(() => {
        const observer = new MutationObserver(() => {
            setTheme(document.body.getAttribute('data-theme') || 'light');
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });
        return () => observer.disconnect();
    }, []);
    const { user: authUser } = useSelector(x => x.auth)
    const url = process.env.REACT_APP_URL

    const [title, setTitle] = useState('')
    const [role, setRole] = useState('')
    const [event, setEvent] = useState('')
    const [position, setPosition] = useState('')
    const [meetingRoles, setMeetingRoles] = useState([])
    const [agendaEvents, setAgendaEvents] = useState([])
    const [saveLoading, setSaveLoading] = useState(false)
    const [selectedEventType, setSelectedEventType] = useState('')

    useEffect(function () {
        if (!item.item_title) setTitle('')
        else setTitle(item.item_title)
        if (!item.meetingRole?._id) setRole('')
        else setRole(item.meetingRole._id)
        if (!item.agendaEvent?._id) { setEvent(''); setSelectedEventType(''); }
        else { setEvent(item.agendaEvent._id); setSelectedEventType(item.agendaEvent.eventType || ''); }
        if (!item.position) setPosition('')
        else setPosition(item.position)
    }, [item.item_title, item.meetingRole?._id, item.agendaEvent?._id, item.agendaEvent?.eventType, item.position])

    useEffect(() => {
        fetch(`${url}/meetingroles`, { method: 'GET' })
            .then(res => res.json())
            .then(data => setMeetingRoles(data))
            .catch(err => console.error(err))
    }, [url])

    useEffect(() => {
        fetch(`${url}/agendaEvents`, { method: 'GET' })
            .then(res => res.json())
            .then(data => setAgendaEvents(data))
            .catch(err => console.error(err))
    }, [url])

    const handleSubmit = (e) => {
        e.preventDefault()
        setSaveLoading(true)
        const validate = true;
        if (validate === true) {
            fetch(`${url}/agendaItem/${item.agendaItemId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + authUser.token
                },
                body: JSON.stringify({
                    item_title: title,
                    meetingRole: role,
                    agendaEvent: event,
                    position: position
                })
            })
                .then(res => {
                    if (res.status !== 200 && res.status !== 201) {
                        throw new Error('(' + res.status + ') We could not save your new agenda Item')
                    }
                    return res.json()
                })
                .then(resData => {
                    setSaveLoading(false)
                    getMeeting(true)
                    handleClose()
                })
                .catch(err => {
                    toast.error('' + err, { position: 'bottom-left' })
                    setSaveLoading(false)
                })
        }
    }

    // Theme-aware react-select styles using CSS variables
    const getSelectStyles = () => {
        const root = document.body;
        const computed = getComputedStyle(root);
        const bg = computed.getPropertyValue('--tm-light-bg') || '#fff';
        const darkBg = computed.getPropertyValue('--tm-dark-bg') || '#23272b';
        const fg = computed.getPropertyValue('--foreground') || '#222';
        const border = computed.getPropertyValue('--modal-borders') || '#ccc';
        const placeholder = computed.getPropertyValue('--tm-gray-text') || '#666';
        // Use theme from state (updates on theme switch)
        const controlBg = theme === 'dark' ? darkBg : bg;
        const menuBg = theme === 'dark' ? darkBg : bg;
        const textColor = theme === 'dark' ? '#fff' : fg;
        const optionSelected = theme === 'dark' ? '#495057' : '#e9ecef';
        const optionFocused = theme === 'dark' ? '#495057' : '#f1f3f5';
        return {
            control: (provided) => ({
                ...provided,
                backgroundColor: controlBg,
                color: textColor,
                borderColor: border,
            }),
            menu: (provided) => ({
                ...provided,
                backgroundColor: menuBg,
                color: textColor,
            }),
            singleValue: (provided) => ({
                ...provided,
                color: textColor,
            }),
            option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected
                    ? optionSelected
                    : state.isFocused
                        ? optionFocused
                        : menuBg,
                color: textColor,
            }),
            input: (provided) => ({
                ...provided,
                color: textColor,
            }),
            placeholder: (provided) => ({
                ...provided,
                color: placeholder,
            }),
        };
    };

    const eventOptions = [...agendaEvents]
        .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')))
        .map(event => ({ value: event._id, label: event.name, eventType: event.eventType }))
    const roleOptions = [...meetingRoles]
        .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')))
        .map(role => ({ value: role._id, label: role.name }))

    return (
        <Card className='my-2 p-3'>
            <Card.Header>Edit Agenda Item</Card.Header>
            <Form onSubmit={handleSubmit}>
                <Card.Body>
                    <InputGroup className='mb-3'>
                        <Form.Control
                            className='formInput'
                            placeholder='Title'
                            aria-label='Title'
                            name='title'
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                        />
                    </InputGroup>
                    <Select
                        options={eventOptions}
                        className='mb-3 formInput'
                        name='event'
                        value={eventOptions.find(option => option.value === event)}
                        onChange={option => { setEvent(option ? option.value : ''); setSelectedEventType(option ? option.eventType : ''); }}
                        isClearable
                        isSearchable
                        styles={getSelectStyles()}
                        placeholder='Select Event'
                    />
                    {selectedEventType && (
                        <div className='mb-3' style={{ fontSize: '0.8rem', fontStyle: 'italic', color: '#FFD700' }}>
                            {selectedEventType}
                        </div>
                    )}
                    <Select
                        options={roleOptions}
                        className='mb-3 formInput'
                        name='role'
                        value={roleOptions.find(option => option.value === role)}
                        onChange={option => setRole(option ? option.value : '')}
                        isClearable
                        isSearchable
                        styles={getSelectStyles()}
                        placeholder='Select Role'
                    />
                    <InputGroup className='mb-3'>
                        <Form.Control
                            className='formInput'
                            placeholder='Position'
                            aria-label='Position'
                            name='position'
                            value={position}
                            onChange={e => setPosition(e.target.value)}
                        />
                    </InputGroup>
                </Card.Body>
                <Card.Footer>
                    {saveLoading
                        ?
                        <>
                            <AppLoader />

                        </>
                        :
                        <>
                            <Button variant="success" type='submit'>
                                <i className='bi bi-save'></i>
                            </Button>{' '}
                            <Button variant="danger" onClick={handleClose} type='button'>
                                <i className='bi bi-x-lg'></i>
                            </Button>
                        </>
                    }
                </Card.Footer>
            </Form>
        </Card>
    );
}